import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

let lastAuthRequiredToastMs = 0;
const AUTH_REQUIRED_TOAST_THROTTLE_MS = 5000;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  const router = inject(Router);

  const showErrorToast = async (translationKey: string): Promise<void> => {
    const { SnackbarService } = await import('@core/services/snackbar/snackbar.service');
    const { TranslateService } = await import('@ngx-translate/core');

    const snackbarService = injector.get(SnackbarService);
    const translateService = injector.get(TranslateService);
    snackbarService.error(translateService.instant(translationKey));
  };

  const clearLocalSession = async (): Promise<void> => {
    const { AuthService } = await import('@core/services/auth/auth.service');
    injector.get(AuthService).clearLocalSession();
  };

  const signOutAndRedirectToLogin = async (): Promise<void> => {
    const { AuthService } = await import('@core/services/auth/auth.service');
    await injector.get(AuthService).signOut();
    await router.navigate(['/auth/login']);
  };

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const now = Date.now();

        // 401 Unauthorized: Session invalid, revoked, or expired
        if (error.status === 401) {
          void clearLocalSession(); // Wipes memory state + sessionStorage

          const currentUrl = router.url;
          if (!currentUrl.startsWith('/auth/login')) {
            if (now - lastAuthRequiredToastMs > AUTH_REQUIRED_TOAST_THROTTLE_MS) {
              lastAuthRequiredToastMs = now;
              void showErrorToast('error.authentication_required');
            }

            void router.navigate(['/auth/login'], {
              queryParams: currentUrl && currentUrl !== '/' ? { returnUrl: currentUrl } : undefined
            });
          }
        }

        // 403 Forbidden: Insufficient permissions
        if (error.status === 403) {
          void signOutAndRedirectToLogin();

          if (now - lastAuthRequiredToastMs > AUTH_REQUIRED_TOAST_THROTTLE_MS) {
            lastAuthRequiredToastMs = now;
            void showErrorToast('error.insufficient_permissions');
          }
        }
      }

      return throwError(() => error);
    })
  );
};
